import type { ApplicationWithStats, LogWithRelations } from '@owl/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Icon } from '../icons';
import { StatusDot } from '../shared';
import type { MobileShellCtx } from './MobileShellCtx';

export function SearchOverlay({ ctx }: { ctx: MobileShellCtx }) {
  const [q, setQ] = useState('');
  const [apps, setApps] = useState<ApplicationWithStats[]>([]);
  const [logs, setLogs] = useState<LogWithRelations[]>([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.listApplications(ctx.environment);
        if (!cancelled) setApps(r.items);
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx.environment]);

  useEffect(() => {
    if (!q || q.length < 2) {
      setLogs([]);
      return;
    }
    const handle = window.setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.listLogs({
          environment: ctx.environment,
          search: q,
          limit: 20,
        });
        setLogs(r.items);
      } catch {
        setLogs([]);
      } finally {
        setLoading(false);
      }
    }, 300);
    return () => window.clearTimeout(handle);
  }, [q, ctx.environment]);

  const filteredApps = useMemo(() => {
    if (!q) return [];
    const lo = q.toLowerCase();
    return apps
      .filter(
        (a) =>
          a.name.toLowerCase().includes(lo) ||
          a.company.toLowerCase().includes(lo) ||
          a.product.toLowerCase().includes(lo) ||
          a.instance.toLowerCase().includes(lo),
      )
      .slice(0, 8);
  }, [apps, q]);

  const hasQuery = q.length >= 2;
  const noResults = hasQuery && !loading && filteredApps.length === 0 && logs.length === 0;

  return (
    <div className="search-overlay">
      <div className="search-input-wrap">
        <Icon.Search size={16} />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar apps, logs…"
          className="search-input"
        />
        <button
          type="button"
          className="sheet-close"
          onClick={ctx.closeSheet}
          aria-label="Cerrar"
          style={{ background: 'var(--bg-2)' }}
        >
          <Icon.X size={14} />
        </button>
      </div>

      <div className="search-results">
        {!q && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg-4)', fontSize: 13 }}>
            Escribe para buscar apps o logs
          </div>
        )}

        {filteredApps.length > 0 && (
          <>
            <div className="search-section">Aplicaciones</div>
            {filteredApps.map((a) => (
              <button
                key={a.id}
                type="button"
                className="search-item"
                onClick={() => {
                  ctx.closeSheet();
                  ctx.navigate({ name: 'appdash', applicationId: a.id, tab: 'logs' });
                }}
              >
                <StatusDot status={a.status} />
                <span>{a.name}</span>
                <span className="sub">
                  {a.product}/{a.company}
                  {a.instance ? `/${a.instance}` : ''}
                </span>
              </button>
            ))}
          </>
        )}

        {hasQuery && (logs.length > 0 || loading) && (
          <>
            <div className="search-section">Logs{loading ? ' · cargando…' : ''}</div>
            {logs.map((l) => (
              <button
                key={l.id}
                type="button"
                className="search-item"
                onClick={() => {
                  ctx.openSheet({ kind: 'log', logId: l.id });
                }}
              >
                <span
                  className="ico"
                  style={{
                    background: l.messageType.color,
                    width: 6,
                    height: 6,
                    borderRadius: 99,
                    flex: '0 0 auto',
                  }}
                />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {l.message}
                </span>
                <span className="sub">{l.application.name}</span>
              </button>
            ))}
          </>
        )}

        {noResults && (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--fg-4)', fontSize: 13 }}>
            Sin resultados
          </div>
        )}
      </div>
    </div>
  );
}
